import { apmCaptureError } from '../apm'
import { i18nLocaleByTenantAndLanguage, type LOCALE } from '../i18n'
import { type TenantLanguage } from '../tenant'

/**
 * takes URL and returns only the path without the host
 *
 * https://shop-apotheke.com/artikel/upm3KWD3G/ -> /artikel/upm3KWD3G/
 */
export const urlGetPathName = (fullUrl: string): string => {
  if (!fullUrl) {
    return ''
  }
  try {
    const url = new URL(fullUrl)

    return url.pathname
  } catch {
    return ''
  }
}

/**
 * Check if the URL given is a valid URL
 */
export const urlIsValid = (url: string): boolean => {
  try {
    return Boolean(new URL(url))
  } catch {
    return false
  }
}

const getUriParts = (
  uri: string,
): Pick<URL, 'hash' | 'pathname' | 'search'> => {
  let url: URL | null = null

  try {
    url = new URL(uri)
  } catch {
    const relativeUri = uri.startsWith('/') ? uri : `/${uri}`

    url = new URL(`https://localhost${relativeUri}`)
  }

  const { hash, pathname, search } = url

  return { hash, pathname, search }
}

const addTrailingSlashToPathname = (pathname: string): string => {
  if (pathname.endsWith('/') || pathname.split('/').at(-1)?.includes('.')) {
    return pathname
  }

  return `${pathname}/`
}

/**
 * Contains languages only for multilingual tenants
 */
const getMultilingualLocaleLanguages: () => Partial<
  Record<LOCALE, TenantLanguage>
> = () =>
  Object.fromEntries(
    Object.values(i18nLocaleByTenantAndLanguage).flatMap((languageRecord) => {
      const languageEntries = Object.entries(languageRecord)

      // Not needed for single language tenant
      if (languageEntries.length === 1) {
        return []
      }

      return languageEntries.map(([language, locale]) => [locale, language])
    }),
  )

const addLanguagePrefixToPathname = (
  pathname: string,
  locale: LOCALE,
): string => {
  const language = getMultilingualLocaleLanguages()[locale]

  if (!language || pathname.startsWith(`/${language}/`)) {
    return pathname
  }

  return `/${language}${pathname}`
}

type NormalizeUriParams = {
  locale?: LOCALE
  uri?: string
}

/**
 * Normalize URIs coming from some API responses:
 *
 *  1. Add the leading slash: `test/?test=1` -> `/test/?test=1`
 *  2. Remove duplicated slashes from `pathname`: `/test//page` -> `/test/page`
 *  3. Add trailing slash: `/test` -> `/test/`
 *  4. Add a language prefix if `locale` is provided for multilingual tenants if needed: `/test/` -> `/fr/test/`
 */
export const urlNormalizeUri = (params: NormalizeUriParams): string => {
  const { locale, uri = '' } = params

  if (!uri && process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console -- notify developer for empty uri handling
    console.trace('No uri provided, please handle it in the caller function')
  }

  const uriParts = getUriParts(uri)

  let { pathname } = uriParts
  const { hash, search } = uriParts

  // There is no practical reason for multiple slashes in the pathname in our app
  pathname = pathname.replaceAll(/\/+/gu, '/')

  pathname = addTrailingSlashToPathname(pathname)

  if (locale) {
    pathname = addLanguagePrefixToPathname(pathname, locale)
  }

  return `${pathname}${search}${hash}`
}

/**
 * Replace the origin of the given URL with the given target origin.
 *
 * @param input Input string which can be either absolute or relative URL
 * @param origin Target origin for URL to replace with
 *
 * @example
 * // returns 'http://localhost:3000/foo/bar'
 * urlReplaceOrigin('https://www.shop-apotheke.com/foo/bar', 'http://localhost:3000')
 */
export const urlReplaceOrigin = (input: string, origin: string): string => {
  if (!URL.canParse(origin)) {
    apmCaptureError(new Error(`Origin "${origin}" has invalid format`))

    return input
  }

  try {
    const url = new URL(input)

    const { host, protocol } = new URL(origin)

    url.protocol = protocol
    url.host = host

    return url.href
  } catch {
    return new URL(input, origin).href
  }
}

/**
 * returns a tuple of first part and rest.
 * useful for e.g. splitting tenant or language from rest
 * '/com/foo/bar': ['/com', '/foo/bar']
 * '/com/de/bar': ['/com', '/de/bar']
 * '/de/bar': ['/de', '/bar']
 * '': ['', ''],
 * '/': ['/', ''],
 * '/test': ['/test', ''],
 * '/test/': ['/test', '/'],
 */
export const urlSplitToHeadAndTail = (url: string): [string, string] => {
  // Find the index of the second '/' (the first non-zero index '/')
  const secondSlashIndex = url.indexOf('/', 1)

  // If there is no second '/', split the URL accordingly
  if (secondSlashIndex === -1) {
    return [url, '']
  }

  // Slice the URL at the second '/'
  const head = url.slice(0, secondSlashIndex)
  const tail = url.slice(secondSlashIndex)

  return [head, tail]
}

/**
 * URL instance to string without host
 * example:
 * const urlInstance = new URL('https://shop-apotheke.com/foo/bar?baz=qux#quux')
 * urlInstanceToStringWithoutHost(urlInstance) // '/foo/bar?baz=qux#quux'
 */
export const urlInstanceToUriString = (url: URL): string => {
  return url.pathname + url.search + url.hash
}

/**
 * urlAddSearchParams('/foo', {a: 'b'}) // /foo?a=b
 * urlAddSearchParams('/foo?b=a', {c: 'd'}) // /foo?a=b&c=d
 */
export const urlAddSearchParams = (
  url: string,
  params: Record<string, string>,
): string => {
  // Define a fallback base URL to handle relative URLs
  const fallbackBase = 'https://fallback'
  const parsedUrl = new URL(url, fallbackBase)

  // Determine if the original URL had host or started with pathname only
  const hasNoHost = parsedUrl.origin === new URL(fallbackBase).origin

  // Append each search parameter
  for (const [key, value] of Object.entries(params)) {
    parsedUrl.searchParams.append(key, value)
  }

  if (hasNoHost) {
    // Return only the pathname and search for relative URLs
    return parsedUrl.pathname + parsedUrl.search
  }

  // Return the full URL for absolute URLs
  return parsedUrl.href
}
